import { useQueryClient } from '@tanstack/react-query';
import { useState, useCallback } from 'react';
import config from 'src/config';
import { useToast } from 'src/context/ToastContext';
import { RebateAgreementListItem } from './types';

interface DuplicateResult {
  sourceAgreementId: string;
  sourceAgreementName: string;
  success: boolean;
  newAgreementId?: string;
  error?: string;
}

interface BulkDuplicateState {
  isLoading: boolean;
  results: DuplicateResult[];
  successCount: number;
  failureCount: number;
}

/**
 * Hook for bulk duplicating rebate agreements.
 * Generates automatic names ({originalName} ({year} Copy))
 * and sets dates to Jan 1 - Dec 31 of the specified year.
 */
export function useBulkDuplicateRebateAgreements() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [state, setState] = useState<BulkDuplicateState>({
    isLoading: false,
    results: [],
    successCount: 0,
    failureCount: 0,
  });

  const bulkDuplicate = useCallback(
    async (agreements: RebateAgreementListItem[], year?: number) => {
      if (agreements.length === 0) return;

      setState({
        isLoading: true,
        results: [],
        successCount: 0,
        failureCount: 0,
      });

      const targetYear = year ?? new Date().getUTCFullYear();
      const startDate = `${targetYear}-01-01`;
      const endDate = `${targetYear}-12-31`;

      const duplicatePromises = agreements.map(async (agreement) => {
        const name = `${agreement.name} (${targetYear} Copy)`;

        try {
          const response = await fetch(
            `${config.apiBaseUrl}/rebates/agreements/duplicate`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({
                sourceAgreementId: agreement.id,
                name,
                startDate,
                endDate,
              }),
            },
          );

          if (!response.ok) {
            const errorData = await response.json();
            return {
              sourceAgreementId: agreement.id,
              sourceAgreementName: agreement.name,
              success: false,
              error: errorData.message || 'Failed to duplicate agreement',
            };
          }

          const data = await response.json();
          return {
            sourceAgreementId: agreement.id,
            sourceAgreementName: agreement.name,
            success: true,
            newAgreementId: data.data?.id,
          };
        } catch (error) {
          return {
            sourceAgreementId: agreement.id,
            sourceAgreementName: agreement.name,
            success: false,
            error:
              error instanceof Error ? error.message : 'Unknown error occurred',
          };
        }
      });

      const results = await Promise.all(duplicatePromises);
      const successCount = results.filter((r) => r.success).length;
      const failureCount = results.filter((r) => !r.success).length;

      setState({
        isLoading: false,
        results,
        successCount,
        failureCount,
      });

      // Show toast with results
      if (failureCount === 0) {
        showToast({
          message: `Successfully duplicated ${successCount} agreement${successCount !== 1 ? 's' : ''} to ${targetYear}`,
          title: 'Bulk Duplicate Complete',
          color: 'success',
          icon: 'check',
        });
      } else if (successCount === 0) {
        showToast({
          message: `Failed to duplicate all ${failureCount} agreement${failureCount !== 1 ? 's' : ''}`,
          title: 'Bulk Duplicate Failed',
          color: 'error',
          icon: 'error',
        });
      } else {
        showToast({
          message: `Duplicated ${successCount} of ${results.length} agreements. ${failureCount} failed.`,
          title: 'Bulk Duplicate Partially Complete',
          color: 'warning',
          icon: 'warning',
        });
      }

      // Invalidate queries to refresh the list
      queryClient.invalidateQueries({ queryKey: ['rebateAgreements'] });

      return results;
    },
    [queryClient, showToast],
  );

  const reset = useCallback(() => {
    setState({
      isLoading: false,
      results: [],
      successCount: 0,
      failureCount: 0,
    });
  }, []);

  return {
    bulkDuplicate,
    reset,
    isLoading: state.isLoading,
    results: state.results,
    successCount: state.successCount,
    failureCount: state.failureCount,
  };
}

export default useBulkDuplicateRebateAgreements;
